import { useMemo } from "react";
import { ChevronDown, Info } from "lucide-react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "src/@/components/ui/table";
import { formatCurrency } from "src/@/lib/format-currency";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "src/@/components/ui/collapsible";
import { Button } from "src/@/components/ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "src/@/components/ui/tooltip";

type ExperimentalSummaryProps = {
    voidData?: {
        orders: number;
        items: number;
        totalSales: number;
    };
    guestData?: {
        totalGuests: number;
        averageTicketPerGuest: number;
    };
    employeeData?: Array<{
        name: string;
        voids: number;
        discounts: number;
        refunds: number;
        voidSales: number;
        discountSales: number;
        refundSales: number;
        sales: number;
        tips: number;
    }>;
    laborData?: {
        totalLaborCost: number;
        laborCostPercentage: number;
        salesPerLaborHour: number;
        totalLaborHours: number;
    };
    completionTimeData?: {
        averageCompletionTime: number;
    };
};

const CollapsibleSection = ({
    title,
    children,
    tooltip,
}: {
    title: string;
    children: React.ReactNode;
    tooltip?: string;
}) => (
        <Collapsible
            className="h-fit w-full rounded-md border"
            defaultOpen={false}
        >
            <CollapsibleTrigger asChild>
                <Button
                    variant="ghost"
                    className="h-10 w-full justify-between rounded-none border-b px-3"
                >
                    <span className="flex items-center gap-1 text-left text-sm font-medium">
                        {title}
                        {tooltip && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <Info className="h-3 w-3" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p className="max-w-xs">{tooltip}</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
                <Table>
                    <TableBody>{children}</TableBody>
                </Table>
            </CollapsibleContent>
        </Collapsible>
    );

export const ExperimentalSalesSummary = ({
    voidData = { orders: 0, items: 0, totalSales: 0 },
    guestData = { totalGuests: 0, averageTicketPerGuest: 0 },
    employeeData = [],
    laborData = {
        totalLaborCost: 0,
        laborCostPercentage: 0,
        salesPerLaborHour: 0,
        totalLaborHours: 0,
    },
    completionTimeData = { averageCompletionTime: 0 },
}: ExperimentalSummaryProps) => {
    // Calculate employee totals
    const employeeTotals = useMemo(() => employeeData.reduce(
            (acc, employee) => ({
                voids: acc.voids + employee.voids,
                voidSales: acc.voidSales + employee.voidSales,
                discounts: acc.discounts + employee.discounts,
                discountSales: acc.discountSales + employee.discountSales,
                refunds: acc.refunds + employee.refunds,
                refundSales: acc.refundSales + employee.refundSales,
                sales: acc.sales + employee.sales,
                tips: acc.tips + employee.tips,
            }),
            {
                voids: 0,
                voidSales: 0,
                discounts: 0,
                discountSales: 0,
                refunds: 0,
                refundSales: 0,
                sales: 0,
                tips: 0,
            },
        ), [employeeData]);

    return (
        <div className="space-y-4">
            <div className="grid w-full auto-rows-min grid-cols-1 gap-4 lg:grid-cols-2">
                {/* Completion Time Section */}
                <CollapsibleSection title={t`Completion Time`}>
                    <>
                        <TableRow>
                            <TableCell><Trans>Average Completion Time</Trans></TableCell>
                            <TableCell className="text-right">
                                {completionTimeData.averageCompletionTime.toFixed(
                                    1,
                                )}{" "}
                                min
                            </TableCell>
                        </TableRow>
                    </>
                </CollapsibleSection>

                {/* Employees Section */}
                <CollapsibleSection
                    title={t`Employees`}
                    tooltip={t`This data is available if you are on register beta. Please contact support to set up.`}
                >
                    <>
                        <TableRow>
                            <TableHead><Trans>Name</Trans></TableHead>
                            <TableHead className="text-right"><Trans>Sales</Trans></TableHead>
                            <TableHead className="text-right"><Trans>Tips</Trans></TableHead>
                            <TableHead className="text-right"><Trans>Voids</Trans></TableHead>
                            <TableHead className="text-right">
                                <Trans>Discounts</Trans>
                            </TableHead>
                            <TableHead className="text-right">
                                <Trans>Refunds</Trans>
                            </TableHead>
                        </TableRow>
                        {employeeData.length > 0 ? (
                            <>
                                {employeeData.map((employee) => (
                                    <TableRow key={employee.name}>
                                        <TableCell>{employee.name}</TableCell>
                                        <TableCell className="text-right">
                                            {formatCurrency(employee.sales)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {formatCurrency(employee.tips)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex flex-col">
                                                <span>{employee.voids}</span>
                                                <span className="text-sm text-muted-foreground">
                                                    {formatCurrency(
                                                        employee.voidSales,
                                                    )}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex flex-col">
                                                <span>
                                                    {employee.discounts}
                                                </span>
                                                <span className="text-sm text-muted-foreground">
                                                    {formatCurrency(
                                                        employee.discountSales,
                                                    )}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex flex-col">
                                                <span>{employee.refunds}</span>
                                                <span className="text-sm text-muted-foreground">
                                                    {formatCurrency(
                                                        employee.refundSales,
                                                    )}
                                                </span>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                <TableRow className="border-t">
                                    <TableCell className="font-medium">
                                        <Trans>Totals</Trans>
                                    </TableCell>
                                    <TableCell className="text-right font-medium">
                                        {formatCurrency(employeeTotals.sales)}
                                    </TableCell>
                                    <TableCell className="text-right font-medium">
                                        {formatCurrency(employeeTotals.tips)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex flex-col font-medium">
                                            <span>{employeeTotals.voids}</span>
                                            <span className="text-sm text-muted-foreground">
                                                {formatCurrency(
                                                    employeeTotals.voidSales,
                                                )}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex flex-col font-medium">
                                            <span>
                                                {employeeTotals.discounts}
                                            </span>
                                            <span className="text-sm text-muted-foreground">
                                                {formatCurrency(
                                                    employeeTotals.discountSales,
                                                )}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex flex-col font-medium">
                                            <span>
                                                {employeeTotals.refunds}
                                            </span>
                                            <span className="text-sm text-muted-foreground">
                                                {formatCurrency(
                                                    employeeTotals.refundSales,
                                                )}
                                            </span>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            </>
                        ) : (
                            <TableRow>
                                <TableCell>No data available</TableCell>
                                <TableCell className="text-right">-</TableCell>
                                <TableCell className="text-right">-</TableCell>
                                <TableCell className="text-right">-</TableCell>
                                <TableCell className="text-right">-</TableCell>
                                <TableCell className="text-right">-</TableCell>
                            </TableRow>
                        )}
                    </>
                </CollapsibleSection>

                {/* Labor Section */}
                <CollapsibleSection
                    title={t`Labor`}
                    tooltip={t`This data is available when you use Employee Clock In on SnackOS.`}
                >
                    <>
                        <TableRow>
                            <TableCell><Trans>Total Labor Cost</Trans></TableCell>
                            <TableCell className="text-right">
                                {formatCurrency(laborData.totalLaborCost)}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><Trans>Total Labor Hours</Trans></TableCell>
                            <TableCell className="text-right">
                                {laborData.totalLaborHours.toFixed(1)}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><Trans>Labor Cost % of Sales</Trans></TableCell>
                            <TableCell className="text-right">
                                {laborData.laborCostPercentage.toFixed(1)}%
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><Trans>Sales per Labor Hour</Trans></TableCell>
                            <TableCell className="text-right">
                                {formatCurrency(laborData.salesPerLaborHour)}
                            </TableCell>
                        </TableRow>
                    </>
                </CollapsibleSection>

                {/* Void Section */}
                <CollapsibleSection title={t`Void`}>
                    <>
                        <TableRow>
                            <TableCell><Trans>Orders</Trans></TableCell>
                            <TableCell className="text-right">
                                {voidData.orders}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><Trans>Items</Trans></TableCell>
                            <TableCell className="text-right">
                                {voidData.items}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><Trans>Voided Sales</Trans></TableCell>
                            <TableCell className="text-right">
                                {formatCurrency(voidData.totalSales)}
                            </TableCell>
                        </TableRow>
                    </>
                </CollapsibleSection>

                {/* Guests Section */}
                <CollapsibleSection
                    title={t`Guests`}
                    tooltip={t`Guest count is only available for dine-in orders`}
                >
                    <>
                        <TableRow>
                            <TableCell><Trans>Total Guests</Trans></TableCell>
                            <TableCell className="text-right">
                                {guestData.totalGuests}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><Trans>Average Sales per Guest</Trans></TableCell>
                            <TableCell className="text-right">
                                {formatCurrency(
                                    guestData.averageTicketPerGuest,
                                )}
                            </TableCell>
                        </TableRow>
                    </>
                </CollapsibleSection>
            </div>
        </div>
    );
};
